import type { Observable } from 'rxjs';
import type { ReactNode } from 'react';
import { from, switchMap, of, distinctUntilChanged, combineLatest, map } from 'rxjs';
// eslint-disable-next-line no-restricted-imports
import accountStore from 'store/account';
// eslint-disable-next-line no-restricted-imports
import user from 'store/user';

import ServiceBase from '../service-base';
import authentication from '../authentication';

class ApplicationHeader extends ServiceBase<ReactNode> {
  name = 'application-header';

  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  private static lazyAuthorizationModule: Promise<typeof import('../authorization')> | null = null;

  constructor() {
    super({ initialState: undefined });
  }

  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  private static async getAuthorizationModule(): Promise<typeof import('../authorization')> {
    if (!this.lazyAuthorizationModule) {
      this.lazyAuthorizationModule = import('../authorization');
    }

    return this.lazyAuthorizationModule;
  }

  /**
   * Business logic for determining whether notifications should be enabled
   * @returns Observable that emits true if the notifications should be enabled, false otherwise
   */
  get showNotifications$(): Observable<boolean> {
    return authentication.event.onChange$.pipe(
      distinctUntilChanged(),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) return of(false);

        return combineLatest([
          accountStore.onEnrollmentComplete$,
          user.onChange$.pipe(
            map((data) => {
              switch (data.userAccountType) {
                case 'studio':
                  return user.isModelView();
                case 'model':
                case 'single':
                default:
                  return true;
              }
            }),
            distinctUntilChanged()
          ),
        ]).pipe(
          map(([hasEnrollmentCompleted, hasRequiredProperties]) => hasEnrollmentCompleted && hasRequiredProperties),
          distinctUntilChanged()
        );
      })
    );
  }

  /**
   * Business logic for determining whether the user badge should be visible
   * @returns Observable that emits true if the user badge should be visible, false otherwise
   */
  get showUserBadge$(): Observable<boolean> {
    return authentication.event.onChange$.pipe(
      distinctUntilChanged(),
      switchMap((isAuthenticated) => (isAuthenticated ? of(true) : of(false)))
    );
  }

  /**
   * Whether to show the "Add new model" button in the user badge menu
   */
  get showAddNewModelButton$(): Observable<boolean> {
    return authentication.event.onChange$.pipe(
      distinctUntilChanged(),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) return of(false);

        return combineLatest([accountStore.onEnrollmentComplete$, user.onChange$]).pipe(
          map(([enrollmentCompleted]) => {
            if (!enrollmentCompleted || !user.isSingleAccountType()) return false;

            return !user.hasMultiplePerformers();
          }),
          distinctUntilChanged()
        );
      })
    );
  }

  /**
   * Whether to show the "Switch to studio" button in the user badge menu
   */
  get showSwitchToStudioButton$(): Observable<boolean> {
    return authentication.event.onChange$.pipe(
      distinctUntilChanged(),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) return of(false);

        return combineLatest([accountStore.onEnrollmentComplete$, user.onChange$]).pipe(
          map(([enrollmentCompleted]) => {
            if (!enrollmentCompleted) return false;

            return user.isStudioAccountType() && user.isModelView();
          }),
          distinctUntilChanged()
        );
      })
    );
  }

  /**
   * Business logic for determining whether the account settings button should be visible
   * @returns Observable that emits true if the account settings button should be visible, false otherwise
   */
  get showAccountSettings$(): Observable<boolean | undefined> {
    return authentication.event.onChange$.pipe(
      distinctUntilChanged(),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          return of(false);
        }

        const enrollmentCompleted$ = accountStore.onEnrollmentComplete$;

        const settingsAuthorized$ = from(ApplicationHeader.getAuthorizationModule()).pipe(
          switchMap((mod) => mod.default.getAuthorization$('settings') ?? of(false))
        );

        return combineLatest([enrollmentCompleted$, settingsAuthorized$]).pipe(
          map(([enrollmentCompleted, authorized]) => Boolean(enrollmentCompleted && authorized)),
          distinctUntilChanged()
        );
      })
    );
  }

  get onContentChange$(): Observable<ReactNode> {
    return super.onChange$;
  }

  setContent(content: ReactNode): void {
    super.set(content);
  }

  clearContent(): void {
    super.set(undefined);
  }
}

export default new ApplicationHeader();
